/**
 * BROWSER-BASED IMAGE RENDERING
 * Renders image with text overlays using html2canvas
 * Supports Unicode/Indic languages with Google Fonts
 */

import html2canvas from 'html2canvas';
import { loadGoogleFonts, FONT_FAMILY } from './fonts';

/**
 * Render image with text overlays
 * Creates a DOM structure and captures it using html2canvas
 *
 * @param {string} imageUrl - Source image URL
 * @param {Array} textLayers - Array of text layer objects
 * @returns {Promise<Blob>} Image blob (PNG format)
 */
export const renderImageWithText = async (imageUrl, textLayers) => {
  console.log('🎨 Starting image rendering...', {
    imageUrl,
    textLayerCount: textLayers.length
  });

  try {
    // Step 1: Load Google Fonts
    await loadGoogleFonts();

    // Step 2: Create offscreen container
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '-9999px';
    container.style.top = '0';
    container.style.overflow = 'hidden';

    // Step 3: Load and add base image
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = imageUrl;

    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = () => reject(new Error('Failed to load image'));
      // Timeout after 30 seconds
      setTimeout(() => reject(new Error('Image load timeout')), 30000);
    });

    const imageWidth = img.width;
    const imageHeight = img.height;

    console.log('✅ Image loaded:', {
      dimensions: `${imageWidth}x${imageHeight}`
    });

    // Set container size to match image
    container.style.width = `${imageWidth}px`;
    container.style.height = `${imageHeight}px`;

    // Add image to container
    img.style.width = '100%';
    img.style.height = '100%';
    img.style.objectFit = 'cover';
    img.style.display = 'block';
    container.appendChild(img);

    // Step 4: Add text layers as DOM elements
    textLayers.forEach((layer, index) => {
      const textDiv = document.createElement('div');
      textDiv.textContent = layer.text;
      textDiv.style.position = 'absolute';
      textDiv.style.left = `${layer.x}%`;
      textDiv.style.top = `${layer.y}%`;
      textDiv.style.transform = 'translate(-50%, -50%)';
      textDiv.style.fontSize = `${layer.fontSize}px`;
      textDiv.style.fontFamily = FONT_FAMILY;
      textDiv.style.fontWeight = layer.bold ? 'bold' : 'normal';
      textDiv.style.fontStyle = layer.italic ? 'italic' : 'normal';
      textDiv.style.color = layer.color;
      textDiv.style.textAlign = layer.align || 'center';
      textDiv.style.whiteSpace = 'pre-wrap';
      textDiv.style.wordWrap = 'break-word';
      textDiv.style.maxWidth = '70%';

      // Enable proper text shaping for complex scripts
      textDiv.style.fontFeatureSettings = '"liga" 1, "calt" 1, "kern" 1';
      textDiv.style.textRendering = 'optimizeLegibility';
      textDiv.style.WebkitFontSmoothing = 'antialiased';
      textDiv.style.MozOsxFontSmoothing = 'grayscale';

      container.appendChild(textDiv);

      console.log(`📝 Text layer ${index + 1}: "${layer.text.substring(0, 30)}"`);
    });

    // Append to body (required for html2canvas)
    document.body.appendChild(container);

    // Small delay to ensure fonts are fully applied
    await new Promise(resolve => setTimeout(resolve, 100));

    // Step 5: Capture with html2canvas
    console.log('📸 Capturing with html2canvas...');

    const canvas = await html2canvas(container, {
      backgroundColor: null,
      logging: false,
      useCORS: true,
      allowTaint: true,
      scale: 1,
      width: imageWidth,
      height: imageHeight
    });

    // Step 6: Remove temporary container
    document.body.removeChild(container);

    // Step 7: Convert canvas to blob
    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            console.log('✅ Image blob created:', {
              size: `${(blob.size / 1024 / 1024).toFixed(2)} MB`,
              type: 'image/png'
            });
            resolve(blob);
          } else {
            reject(new Error('Failed to create blob'));
          }
        },
        'image/png',
        1.0
      );
    });

    console.log('🎉 Image rendering complete!');
    return blob;

  } catch (error) {
    console.error('❌ Image rendering error:', error);

    // Clean up on error
    const container = document.querySelector('div[style*="-9999px"]');
    if (container && container.parentNode) {
      container.parentNode.removeChild(container);
    }

    throw error;
  }
};
